// hooks/api/batch.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as batchService from "@/services/batch.service";
import {
  BatchParamsType,
  BatchStatus,
  BatchUpdateType,
  ProgramBatchCreateType,
} from "@/services/batch.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const batchesKey = {
  all: ["batches"] as const,
  lists: (filters: BatchParamsType) =>
    [...batchesKey.all, "lists", filters] as const,
  detail: (id: string) => [...batchesKey.all, "detail", id] as const,
};

export const useBatchesQuery = (filters: BatchParamsType) =>
  useQuery({
    queryKey: batchesKey.lists(filters),
    queryFn: () => batchService.fetchAllBatches(filters),
    placeholderData: (prev) => prev,
  });

export const useBatchByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: batchesKey.detail(id),
    queryFn: () => batchService.fetchBatchById(id),
    enabled: enabled && !!id,
  });

/** GET /v1/programs/:programId/batches */
export const useProgramBatchesQuery = (programId: string, enabled = true) =>
  useQuery({
    queryKey: [...batchesKey.all, "program", programId] as const,
    queryFn: () => batchService.fetchProgramBatches(programId),
    enabled: enabled && !!programId,
  });

/** PATCH /v1/batches/:id */
export const useUpdateBatchMutation = (filters: BatchParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BatchUpdateType }) =>
      batchService.updateBatch(id, data),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: batchesKey.lists(filters) });
      queryClient.invalidateQueries({ queryKey: batchesKey.detail(id) });
      notifySuccess("Batch updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update batch"),
  });
};

export const useCreateProgramBatchMutation = (filters: BatchParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, data }: { programId: string; data: ProgramBatchCreateType }) =>
      batchService.createProgramBatch(programId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey.lists(filters) });
      notifySuccess("Batch created successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to create batch"),
  });
};

/** DELETE /v1/batches/:id */
export const useDeleteBatchMutation = (filters: BatchParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => batchService.deleteBatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey.lists(filters) });
      notifySuccess("Batch deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete batch"),
  });
};

/** PATCH /v1/batches/:id/status */
export const useUpdateBatchStatusMutation = (batchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: BatchStatus) =>
      batchService.updateBatchStatus(batchId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey.detail(batchId) });
      queryClient.invalidateQueries({ queryKey: batchesKey.all });
      notifySuccess("Batch status updated");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update status"),
  });
};

/** PATCH /v1/batches/:id/advance-semester */
export const useAdvanceBatchSemesterMutation = (batchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => batchService.advanceBatchSemester(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey.detail(batchId) });
      notifySuccess("Semester advanced successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to advance semester"),
  });
};

/** PATCH /v1/batches/:id/semester — set semester to a specific value */
export const useUpdateBatchSemesterMutation = (batchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (current_semester: number) =>
      batchService.updateBatchSemester(batchId, current_semester),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey.detail(batchId) });
      notifySuccess("Semester updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update semester"),
  });
};
